import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCirclePlus, LuEraser, LuLoaderCircle, LuSave } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { Field, SelectInput, TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useProjectFinanceText from './projectFinanceText';

const FORM_ID = 'project-budget-form';

const EMPTY_FORM = {
  BütçeAdı: '',
  BütçeKaynak: '',
  BütçeSahibi: '',
  BütçeTipi: '',
  BütçeKategorisi: '',
  GiderTipi: '',
  ParaBirimi: '',
  KDV: '',
  BütçeMiktarı: '',
  BütçeTarihi: '',
  BeklenenÖdemeTarihi: '',
  ÖdemeDurumu: '',
};

// Seçeneklerin kayıtlı değerleri (eski formdaki gibi)
const OPTIONS = {
  BütçeKaynak: [['kaynak1', 'Kaynak 1'], ['kaynak2', 'Kaynak 2'], ['kaynak3', 'Kaynak 3']],
  BütçeTipi: [['tip1', 'Tip 1'], ['tip2', 'Tip 2'], ['tip3', 'Tip 3']],
  BütçeKategorisi: [['kategori1', 'Kategori 1'], ['kategori2', 'Kategori 2'], ['kategori3', 'Kategori 3']],
  GiderTipi: [['kira', 'Kira'], ['elektrik', 'Elektrik'], ['diğer giderler', 'Diğer Giderler']],
  ParaBirimi: [['TRY', 'TRY'], ['USD', 'USD'], ['EUR', 'EUR']],
  ÖdemeDurumu: [['Ödendi', 'Ödendi'], ['Beklemede', 'Beklemede'], ['İptal', 'İptal']],
};

// Alan anahtarı → metin anahtarı (Türkçe alan adları kayıtlı değerdir)
const FIELD_KEYS = {
  BütçeAdı: 'name',
  BütçeKaynak: 'source',
  BütçeSahibi: 'owner',
  BütçeTipi: 'type',
  BütçeKategorisi: 'category',
  GiderTipi: 'expenseType',
  ParaBirimi: 'currency',
  KDV: 'vat',
  BütçeMiktarı: 'amount',
  BütçeTarihi: 'date',
  BeklenenÖdemeTarihi: 'expectedDate',
  ÖdemeDurumu: 'paymentStatus',
};

/**
 * Bütçe Ekle: a Budgets record for the project with the old form's fields
 * and options (all required). Clear empties the form.
 */
const AddBudgetModal = ({ isOpen, onClose, onSubmit, projectId, projectName }) => {
  const { language, label } = useProjectFinanceText();
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const querySnapshot = await getDocs(query(collection(db, 'Employees'), orderBy('eName')));
        setEmployees(querySnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() })));
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error(label('addBudget.employeesError'));
      }
    };
    if (isOpen) fetchEmployees();
  }, [isOpen, label]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleReset = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = {};
    Object.keys(EMPTY_FORM).forEach((key) => {
      if (!String(formData[key]).trim()) newErrors[key] = label('addBudget.required');
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(label('addBudget.fillAll'));
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'Budgets'), {
        ...formData,
        projectId, // the project of the budget
        createdAt: new Date().toISOString(),
      });
      toast.success(label('addBudget.added'));
      const submitted = formData;
      handleReset();
      onSubmit(submitted);
      onClose();
    } catch (error) {
      console.error('Error adding budget:', error);
      toast.error(label('addBudget.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldProps = (name) => ({
    label: label(`addBudget.fields.${FIELD_KEYS[name]}`),
    htmlFor: `budget-${FIELD_KEYS[name]}`,
    required: true,
    error: errors[name] || undefined,
  });

  const select = (name, options) => (
    <Field {...fieldProps(name)}>
      <SelectInput
        id={`budget-${FIELD_KEYS[name]}`}
        value={formData[name]}
        onChange={(event) => handleChange(name, event.target.value)}
        placeholder={label('common.choose')}
        options={options}
        error={errors[name] || undefined}
      />
    </Field>
  );

  const optionList = (name) => OPTIONS[name].map(([value, text]) => ({ value, label: text }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="form"
      dismissible={!isSubmitting}
      title={label('addBudget.title')}
      subtitle={projectName}
      closeLabel={label('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
          <LuCirclePlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>{label('common.close')}</Button>
          <Button onClick={handleReset} disabled={isSubmitting}>
            <LuEraser aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('common.clear')}
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={isSubmitting}>
            {isSubmitting ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSubmitting ? label('addBudget.adding') : label('addBudget.submit')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="tw-grid tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
        <Field {...fieldProps('BütçeAdı')} className="sm:tw-col-span-2 lg:tw-col-span-1">
          <TextInput
            id="budget-name"
            value={formData.BütçeAdı}
            onChange={(event) => handleChange('BütçeAdı', event.target.value)}
            placeholder={label('addBudget.namePlaceholder')}
            error={errors.BütçeAdı || undefined}
          />
        </Field>
        {select('BütçeKaynak', optionList('BütçeKaynak'))}
        {select('BütçeSahibi', employees.map((employee) => ({ value: employee.id, label: `${employee.eName || ''} ${employee.eSurname || ''}`.trim() })))}
        {select('BütçeTipi', optionList('BütçeTipi'))}
        {select('BütçeKategorisi', optionList('BütçeKategorisi'))}
        {select('GiderTipi', optionList('GiderTipi'))}
        {select('ParaBirimi', optionList('ParaBirimi'))}
        <Field {...fieldProps('BütçeMiktarı')}>
          <TextInput
            id="budget-amount"
            type="number"
            inputMode="decimal"
            value={formData.BütçeMiktarı}
            onChange={(event) => handleChange('BütçeMiktarı', event.target.value)}
            placeholder="0.00"
            error={errors.BütçeMiktarı || undefined}
            className="tw-tabular-nums"
          />
        </Field>
        <Field {...fieldProps('KDV')}>
          <TextInput
            id="budget-vat"
            type="number"
            inputMode="decimal"
            value={formData.KDV}
            onChange={(event) => handleChange('KDV', event.target.value)}
            placeholder="0"
            error={errors.KDV || undefined}
            className="tw-tabular-nums"
          />
        </Field>
        <Field {...fieldProps('BütçeTarihi')}>
          <TextInput id="budget-date" type="date" value={formData.BütçeTarihi} onChange={(event) => handleChange('BütçeTarihi', event.target.value)} error={errors.BütçeTarihi || undefined} />
        </Field>
        <Field {...fieldProps('BeklenenÖdemeTarihi')}>
          <TextInput id="budget-expectedDate" type="date" value={formData.BeklenenÖdemeTarihi} onChange={(event) => handleChange('BeklenenÖdemeTarihi', event.target.value)} error={errors.BeklenenÖdemeTarihi || undefined} />
        </Field>
        {select('ÖdemeDurumu', optionList('ÖdemeDurumu'))}
      </form>
    </Modal>
  );
};

AddBudgetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string,
};

export default AddBudgetModal;
